import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { generateMockReceipts, generateMockTransactions } from "@/data/payments-mock-data";
import type { Receipt, Transaction } from "@/types/payments";
import type { ReceiptApi } from "@/types/api/receipts";
import type { TransactionApi } from "@/types/api/transactions";
import type { CreateAllocationsRequest, CreateAllocationsResponseApi } from "@/types/api/allocations";

// Map API receipt to UI receipt type
function mapReceiptApiToUi(apiReceipt: ReceiptApi): Receipt {
  return {
    id: apiReceipt.id,
    receiptNumber: apiReceipt.receiptNumber,
    customerId: apiReceipt.customerId,
    customerName: apiReceipt.customerName,
    customerAccountNumber: apiReceipt.customerAccountNumber,
    receiptDate: new Date(apiReceipt.receiptDate),
    receivedAmount: apiReceipt.receivedAmount,
    allocatedAmount: apiReceipt.allocatedAmount,
    unappliedAmount: apiReceipt.unappliedAmount,
    currency: apiReceipt.currency,
    paymentMethod: apiReceipt.paymentMethod as Receipt["paymentMethod"],
    paymentReference: apiReceipt.paymentReference,
    bankReference: apiReceipt.bankReference,
    allocationStatus: apiReceipt.allocationStatus as Receipt["allocationStatus"],
    reconciliationStatus: apiReceipt.reconciliationStatus as Receipt["reconciliationStatus"],
    financeSystemId: apiReceipt.financeSystemId ?? "",
    importedAt: new Date(apiReceipt.importedAt ?? apiReceipt.createdAt),
    createdBy: apiReceipt.createdBy,
    createdAt: new Date(apiReceipt.createdAt),
    hasPromise: false,
  };
}

// Map API transaction to UI transaction type
function mapTransactionApiToUi(apiTransaction: TransactionApi): Transaction {
  const dueDate = apiTransaction.dueDate ? new Date(apiTransaction.dueDate) : new Date();
  const now = new Date();
  const daysOverdue = Math.max(0, Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));

  return {
    id: apiTransaction.id,
    transactionNumber: apiTransaction.referenceNumber,
    transactionType: apiTransaction.transactionType.toLowerCase() as Transaction["transactionType"],
    customerId: apiTransaction.customerId,
    customerName: apiTransaction.customerName,
    transactionDate: new Date(apiTransaction.transactionDate),
    dueDate,
    originalAmount: apiTransaction.amount,
    currentBalance: apiTransaction.outstandingAmount,
    currency: apiTransaction.currency,
    daysOverdue,
    onDispute: apiTransaction.onDispute,
    isPromised: false,
  };
}

/**
 * Hook to fetch a single receipt by ID for allocation
 * Handles both Demo and Live modes
 */
export function useReceiptForAllocation(receiptId: number | undefined) {
  const { apiGet } = useApi();
  const mockReceipts = generateMockReceipts();
  const mockReceipt = mockReceipts.find(r => r.id === receiptId) ?? null;

  return useModeAwareQuery<Receipt | null, Error>(
    ["receipt", receiptId],
    async ({ signal }) => {
      if (!receiptId) return null;
      const response = await apiGet<ReceiptApi>(`/api/v1/receipts/${receiptId}`, undefined, signal);
      return mapReceiptApiToUi(response);
    },
    mockReceipt,
    {
      enabled: !!receiptId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to fetch transactions for a customer (for allocation)
 * Handles both Demo and Live modes
 */
export function useTransactionsForAllocation(customerId: number | undefined) {
  const { apiGet } = useApi();
  const mockTransactions = customerId ? generateMockTransactions(customerId) : [];

  return useModeAwareQuery<Transaction[], Error>(
    ["transactions-for-allocation", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<{ data: TransactionApi[] }>(
        "/api/v1/transactions",
        { customerId: String(customerId), pageSize: 100 },
        signal
      );
      // Filter to only invoices with outstanding balance
      return response.data
        .filter(x => x.transactionType.toLowerCase() === "invoice" && x.outstandingAmount > 0)
        .map(mapTransactionApiToUi);
    },
    mockTransactions.filter(t => t.transactionType === "invoice" && t.currentBalance > 0),
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to create allocations
 * Handles both Demo and Live modes
 */
export function useCreateAllocations() {
  const { apiPost } = useApi();

  return useModeAwareMutation<CreateAllocationsResponseApi, Error, CreateAllocationsRequest>(
    async (request) => {
      return apiPost<CreateAllocationsResponseApi>(
        "/api/v1/allocations",
        request
      );
    },
    async (request) => {
      // Mock response for demo mode
      return {
        receiptId: request.receiptId,
        allocations: request.allocations.map((a, index) => ({
          id: Date.now() + index,
          receiptId: request.receiptId,
          transactionId: a.transactionId,
          allocatedAmount: a.allocatedAmount,
          allocationDate: new Date().toISOString(),
          allocationMethod: a.allocationMethod,
          notes: a.notes,
          createdBy: "demo-user",
          createdAt: new Date().toISOString(),
        })),
        receiptSummary: {
          receivedAmount: 0,
          allocatedAmount: request.allocations.reduce((sum, a) => sum + a.allocatedAmount, 0),
          unappliedAmount: 0,
          allocationStatus: "fully_allocated",
        },
      };
    }
  );
}

/**
 * Combined hook for allocation workspace data
 * Returns receipt, transactions, and mutation for creating allocations
 */
export function useAllocationWorkspaceData(receiptId: number | undefined) {
  const receiptQuery = useReceiptForAllocation(receiptId);
  const customerId = receiptQuery.data?.customerId;
  const transactionsQuery = useTransactionsForAllocation(customerId);
  const createAllocationsMutation = useCreateAllocations();

  return {
    receipt: receiptQuery.data,
    transactions: transactionsQuery.data ?? [],
    isLoading: receiptQuery.isLoading || (receiptQuery.data && transactionsQuery.isLoading),
    isLoadingReceipt: receiptQuery.isLoading,
    isLoadingTransactions: transactionsQuery.isLoading,
    error: receiptQuery.error || transactionsQuery.error,
    createAllocations: createAllocationsMutation.mutateAsync,
    isSubmitting: createAllocationsMutation.isPending,
    refetch: () => {
      receiptQuery.refetch();
      transactionsQuery.refetch();
    },
  };
}
